import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  normalizeReferralCode,
  REFERRAL_ATTRIBUTION_WINDOW_MS,
} from "@petergok/blue-collar-shared";
import { redeemReferral } from "@/api/fetch";
import { useRootStore } from "@/state/RootStore";
import { captureEvent } from "@/utils/posthog";

// Single source of truth for the pending-referral localStorage keys — the
// landing page and the ?ref= capture in App.tsx both write through
// capturePendingReferral below.
const PENDING_CODE_KEY = "pending_referral_code";
const PENDING_META_KEY = "pending_referral_meta";

type PendingReferralMeta = { firstSeenAt: number };

/**
 * Normalizes and persists a pending referral code. The meta record (first
 * time we ever saw a code) is only written once so the 48h attribution
 * ceiling can't be reset by re-visiting a referral link.
 */
export function capturePendingReferral(raw: string | null) {
  const code = normalizeReferralCode(raw);
  if (!code) return;
  try {
    localStorage.setItem(PENDING_CODE_KEY, code);
    if (!localStorage.getItem(PENDING_META_KEY)) {
      localStorage.setItem(
        PENDING_META_KEY,
        JSON.stringify({ firstSeenAt: Date.now() })
      );
    }
  } catch {
    // Storage can be unavailable (private mode) — attribution is best-effort.
  }
}

function readPendingCode(): string | null {
  try {
    return normalizeReferralCode(localStorage.getItem(PENDING_CODE_KEY));
  } catch {
    return null;
  }
}

function readPendingMeta(): PendingReferralMeta | null {
  try {
    const raw = localStorage.getItem(PENDING_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.firstSeenAt === "number" ? parsed : null;
  } catch {
    return null;
  }
}

function clearPendingReferral() {
  try {
    localStorage.removeItem(PENDING_CODE_KEY);
    localStorage.removeItem(PENDING_META_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

// In-session backoff for 5xx/network failures; after the last retry we give
// up until the next page load (the keys survive, so the hook re-runs then).
const RETRY_DELAYS_MS = [1_000, 10_000, 60_000];

/**
 * Post-auth referral redemption. Runs once per session as soon as the user
 * is signed in and a pending code exists. Both 2xx and typed 4xx responses
 * are final outcomes (redeemReferral only throws on 5xx/network), so the
 * pending keys are cleared on any resolved response. Failures are silent to
 * the user — attribution is best-effort.
 */
export function usePendingReferral() {
  const { isSignedIn } = useAuth();
  const store = useRootStore();
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!isSignedIn || attemptedRef.current) return;

    const code = readPendingCode();
    if (!code) return;
    attemptedRef.current = true;

    const meta = readPendingMeta();
    if (meta && Date.now() - meta.firstSeenAt > REFERRAL_ATTRIBUTION_WINDOW_MS) {
      clearPendingReferral();
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const attempt = async (retryIndex: number) => {
      try {
        const res = await redeemReferral({
          code,
          attributionSource: "web_ref",
        });
        // Resolved either way — the server has recorded the outcome, so the
        // pending keys are spent even if the component unmounted mid-flight.
        clearPendingReferral();
        if (res.ok === true) {
          captureEvent("referral_redeem_success", {
            referral_code: code,
            platform: "web",
          });
          // Refresh purchase state so the trial offer shows without a reload.
          if (!cancelled) store.mainAppStore.checkPurchaseState();
        } else {
          captureEvent("referral_redeem_fail", {
            referral_code: code,
            platform: "web",
            reason: res.reason,
          });
        }
      } catch {
        if (cancelled) return;
        if (retryIndex < RETRY_DELAYS_MS.length) {
          timeoutId = setTimeout(
            () => attempt(retryIndex + 1),
            RETRY_DELAYS_MS[retryIndex]
          );
        }
        // Out of retries: keep the keys for the next page load.
      }
    };

    attempt(0);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isSignedIn, store]);
}
